// Small status pills used across the app.

import { Link } from "react-router-dom";
import type { RetentionColor } from "../lib/types";
import { RETENTION_LABELS, RETENTION_TITLES } from "../lib/retention";

/**
 * Retention color dot (🟢🟡🔴 as a small filled circle). Renders nothing for
 * null (client with no visits — no rhythm to judge). Color always comes from
 * lib/retention.ts retentionColor(), never from stored state.
 */
export function RetentionDot({
  color,
  title,
}: {
  color: RetentionColor | null | undefined;
  title?: string;
}) {
  if (!color) return null;
  return (
    <span
      className={`retention-dot dot-${color}`}
      title={title ?? RETENTION_TITLES[color]}
      role="img"
      aria-label={`Retention: ${RETENTION_LABELS[color]}`}
    />
  );
}

/**
 * Active-package balance pill. Renders nothing when balance is null/undefined
 * (no active package). Green when healthy, amber at the low-balance threshold
 * (≤1), red at 0 or below.
 */
export function PackageBadge({ balance }: { balance: number | null | undefined }) {
  if (balance == null) return null;
  const cls =
    balance <= 0 ? "badge badge-red" : balance <= 1 ? "badge badge-amber" : "badge badge-green";
  return (
    <span className={cls} title={`Active package · ${balance} drip${balance === 1 ? "" : "s"} left`}>
      Pkg · {balance}
    </span>
  );
}

/**
 * Rebook-due pill. Renders nothing until weeks_since_last_visit reaches the
 * threshold (default 6); amber when due, red at 2× the threshold.
 */
export function DueBadge({
  weeks,
  threshold = 6,
}: {
  weeks: number | null | undefined;
  threshold?: number;
}) {
  if (weeks == null || weeks < threshold) return null;
  const cls = weeks >= threshold * 2 ? "badge badge-red" : "badge badge-amber";
  return (
    <span className={cls} title={`Last visit ${weeks} weeks ago`}>
      Due · {weeks}w
    </span>
  );
}

const SOURCE_LABELS: Record<string, string> = {
  google_ads: "Google Ads",
  seo_gbp: "SEO (GBP)",
  referral: "Referral",
  organic: "Organic",
  other: "Other",
};

/** Human labels for v_client_rfm.rfm_segment values. */
export const SEGMENT_LABELS: Record<string, string> = {
  new: "New",
  champion: "Champion",
  slipping_away: "Slipping away",
  win_back: "Win back",
  one_and_done: "One & done",
  regular: "Regular",
};

const SEGMENT_TITLES: Record<string, string> = {
  new: "First visit within 30 days",
  champion: "Frequent and on their usual rhythm",
  slipping_away: "Later than their usual rhythm (1.5–3×)",
  win_back: "Way past their usual rhythm (>3×)",
  one_and_done: "One visit, 45+ days ago",
  regular: "Everything else",
};

/**
 * RFM segment chip — distinct pastel fill with dark ink text per segment
 * (champion=mint, slipping_away=peach, win_back=rose, new=sky,
 * one_and_done=lavender, regular=neutral). Renders nothing when null.
 *
 * With `link`, the chip renders as a Link to the Clients list pre-filtered to
 * this RFM segment (/clients?rfm=…); clicks stop propagating so row-click
 * navigation underneath keeps working.
 */
export function SegmentChip({
  segment,
  link = false,
}: {
  segment: string | null | undefined;
  link?: boolean;
}) {
  if (!segment) return null;
  const known = segment in SEGMENT_LABELS;
  const cls = `seg-chip ${known ? `seg-${segment}` : "seg-regular"}`;
  const label = SEGMENT_LABELS[segment] ?? segment;
  if (link) {
    const why = SEGMENT_TITLES[segment];
    return (
      <Link
        to={`/clients?rfm=${encodeURIComponent(segment)}`}
        className={cls}
        style={{ textDecoration: "none", cursor: "pointer" }}
        title={`${why ? `${why} — ` : ""}view these clients`}
        onClick={(e) => e.stopPropagation()}
      >
        {label}
      </Link>
    );
  }
  return (
    <span className={cls} title={SEGMENT_TITLES[segment]}>
      {label}
    </span>
  );
}

/** Butter/gold whale chip; renders nothing unless whale is true. */
export function WhaleBadge({ whale }: { whale: boolean | null | undefined }) {
  if (!whale) return null;
  return (
    <span className="seg-chip seg-whale" title="High value: avg ticket ≥ $600 or LTV ≥ $2,000">
      Whale
    </span>
  );
}

/** Acquisition-source pill; renders nothing for null/empty source. */
export function SourceBadge({ source }: { source: string | null | undefined }) {
  if (!source) return null;
  const label = SOURCE_LABELS[source] ?? source;
  const cls =
    source === "google_ads"
      ? "badge badge-accent"
      : source === "seo_gbp"
        ? "badge badge-mint"
        : source === "referral"
          ? "badge badge-green"
          : "badge badge-muted";
  return <span className={cls}>{label}</span>;
}
